import React, { useState, useRef, useMemo } from 'react'
import { Box, Paper, Typography, Grid, LinearProgress, Chip, IconButton, Popper } from '@mui/material'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback'
import CallMissedIcon from '@mui/icons-material/CallMissed'
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CancelIcon from '@mui/icons-material/Cancel'
import { operatorReportStyles as opStyles } from '../../../helpers/style'
import { reportFromFacets } from './reportFacets'

export default function ReportPerformance({
  report,
  workerRating,
  opTr,
  langIndex,
  getCallTypeLabel,
  historyData,
  historyFacetFields,

}) {
  const [helpAnchor, setHelpAnchor] = useState(null)
  const helpCloseTimeoutRef = useRef(null)
  const handleHelpOpen = (e) => {
    if (helpCloseTimeoutRef.current) clearTimeout(helpCloseTimeoutRef.current)
    setHelpAnchor(e.currentTarget)
  }
  const handleHelpClose = () => {
    helpCloseTimeoutRef.current = setTimeout(() => setHelpAnchor(null), 150)
  }
  const handleHelpCloseClear = () => {
    if (helpCloseTimeoutRef.current) clearTimeout(helpCloseTimeoutRef.current)
  }

  const facetFields = historyFacetFields ?? historyData?.facet_counts?.facet_fields ?? null
  const displayReport = useMemo(
    () => reportFromFacets(report, facetFields),
    [report, facetFields]
  )

  return (
    <Box id="section-rating" sx={opStyles.sectionBox}>
      <Paper sx={opStyles.performancePaper(displayReport.received, displayReport.answered)}>
        <Box sx={opStyles.performanceTitleBox}>
          <EmojiEventsIcon sx={opStyles.performanceIcon} />
          <Typography variant="subtitle1" sx={opStyles.performanceSubtitle}>
            {opTr.performanceTitle?.[langIndex]}
          </Typography>
          <IconButton
            size="small"
            sx={{ color: '#7ebc0a' }}
            onMouseEnter={handleHelpOpen}
            onMouseLeave={handleHelpClose}
            aria-label={opTr.performanceHelpTooltip?.[langIndex] || 'How it works'}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Box>

        <Popper
          open={Boolean(helpAnchor)}
          anchorEl={helpAnchor}
          placement="bottom-start"
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        >
          <Paper
            elevation={8}
            onMouseEnter={handleHelpCloseClear}
            onMouseLeave={handleHelpClose}
            sx={{ mt: 0, overflow: 'visible', width: 400 }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                {opTr.performanceHelpTitle?.[langIndex] || 'How to read this section'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {opTr.performanceHelpDesc?.[langIndex] ||
                  'This block shows how many calls were answered vs not answered, busy, failed, or canceled in the selected period. If callers rated the employee after a call, the average rating and number of ratings are also shown.'}
              </Typography>
            </Box>
          </Paper>
        </Popper>
        {displayReport.received === 0 ? (
          <Typography color="text.secondary">
            {opTr.noCallsPeriod?.[langIndex]}
          </Typography>
        ) : (
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="body1" sx={opStyles.performanceBody}>
                {(opTr.answeredOfCalls?.[langIndex] ?? 'Answered {0} of {1} calls ({2}%)')
                  .replace('{0}', displayReport.answered)
                  .replace('{1}', displayReport.received)
                  .replace('{2}', displayReport.received ? Math.round((displayReport.answered / displayReport.received) * 100) : 0)}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={opStyles.performanceBodySecondary}>
                {getCallTypeLabel('not_answered')}: {displayReport.notAnswered} · {getCallTypeLabel('busy')}: {displayReport.busy} · {getCallTypeLabel('failed')}: {displayReport.failed} · {getCallTypeLabel('canceled')}: {displayReport.canceled}
                {workerRating.count > 0 && (
                  <>
                    {' · '}
                    {opTr.averageRating?.[langIndex]}:{' '}
                    <Box component="span" sx={opStyles.performanceRatingSpan}>
                      {workerRating.average.toFixed(1)} ★
                    </Box>
                    {' '}({workerRating.count} {opTr.rated?.[langIndex]})
                  </>
                )}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={displayReport.received ? Math.round((displayReport.answered / displayReport.received) * 100) : 0}
                sx={opStyles.performanceLinearProgress(displayReport.received, displayReport.answered)}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={opStyles.chipsBox}>
                <Chip size="small" icon={<PhoneCallbackIcon sx={opStyles.chipIcon} />} label={`${displayReport.answered} ${getCallTypeLabel('answered')}`} sx={opStyles.chipAnswered} />
                <Chip size="small" icon={<CallMissedIcon sx={opStyles.chipIcon} />} label={`${displayReport.notAnswered} ${getCallTypeLabel('not_answered')}`} sx={opStyles.chipNotAnswered} />
                <Chip size="small" icon={<PhoneDisabledIcon sx={opStyles.chipIcon} />} label={`${displayReport.busy} ${getCallTypeLabel('busy')}`} sx={opStyles.chipBusy} />
                <Chip size="small" icon={<ErrorOutlineIcon sx={opStyles.chipIcon} />} label={`${displayReport.failed} ${getCallTypeLabel('failed')}`} sx={opStyles.chipFailed} />
                <Chip size="small" icon={<CancelIcon sx={opStyles.chipIcon} />} label={`${displayReport.canceled} ${getCallTypeLabel('canceled')}`} sx={opStyles.chipCanceled} />
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  )
}
